import type { OrgMemberSummary } from '@nadoutong/contracts';
import type { FormEvent } from 'react';
import { useState } from 'react';
import { localizeCaughtError } from '../../api/errorMessages.ts';
import { useDisplayRate } from '../../api/useBilling.ts';
import { useUpdateOrgMember } from '../../api/useOrgs.ts';
import { Modal } from '../../components/Modal.tsx';
import {
  formatLimitInputValue,
  isValidDisplayRate,
  parseSpendLimitInput,
} from '../../components/spendLimit.ts';
import type { SpendLimitParseCode } from '../../components/spendLimit.ts';
import { useToast } from '../../components/Toast.tsx';
import type { I18nKey } from '../../i18n/index.ts';
import { useT } from '../../i18n/index.ts';
import { Button } from '../../ui/index.ts';

/**
 * platform-api stores a member ceiling as a positive integer of USD cents,
 * or `null` for no ceiling. `0` and anything above this bound are 400s
 * (`normaliseMonthlyLimitInput` in v1-orgs).
 */
const MEMBER_LIMIT_MAX_USD_CENTS = 999_999_999_999;

const LIMIT_ERROR_KEY: Readonly<Record<SpendLimitParseCode, I18nKey>> = {
  invalid: 'organization.members.limit.error.invalid',
  zero: 'organization.members.limit.error.zero',
  rate_unavailable: 'keys.limit_error.rate_unavailable',
};

const INPUT_CLASS =
  'h-9 w-full rounded border border-line bg-raised px-3 font-mono text-body tabular-nums text-ink placeholder:text-ink-faint transition-colors duration-150 hover:border-line-strong focus:border-forest focus:outline-none focus-visible:ring-2 focus-visible:ring-forest-bright/30 disabled:pointer-events-none disabled:opacity-60';

interface Props {
  readonly orgId: string;
  readonly member: OrgMemberSummary;
  readonly onClose: () => void;
}

/**
 * Set or clear one member's monthly spend ceiling.
 *
 * The PATCH accepts the ceiling for every member the caller may manage,
 * including the org owner and the caller themselves — platform-api only
 * refuses owner *status* and *role* changes. The dialog therefore does
 * not special-case those rows.
 */
export function MemberLimitDialog({ orgId, member, onClose }: Props): JSX.Element {
  const t = useT();
  const toast = useToast();
  const updateMember = useUpdateOrgMember();
  const displayRateQ = useDisplayRate();
  const cnyPerUsd = displayRateQ.data?.cnyPerUsd ?? null;
  const rateReady = isValidDisplayRate(cnyPerUsd);
  const memberName = member.displayName ?? member.email;
  const prefill = rateReady ? formatLimitInputValue(member.monthlyLimitUsdCents, cnyPerUsd) : '';
  // Null until the first keystroke, so a rate that arrives after open
  // can still fill the field. A snapshot taken at click time would be
  // the empty string and a save would clear a ceiling nobody edited.
  const [draft, setDraft] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const value = draft ?? prefill;
  const pending = updateMember.isPending;
  const titleId = 'organization-member-limit-title';
  const descriptionId = 'organization-member-limit-description';

  function close(): void {
    if (!pending) onClose();
  }

  async function submit(monthlyLimitUsdCents: number | null): Promise<void> {
    setError(null);
    try {
      await updateMember.mutateAsync({
        orgId,
        accountId: member.accountId,
        body: { monthlyLimitUsdCents },
      });
      toast.success(t('organization.members.limit.toast.updated'));
      onClose();
    } catch (err) {
      setError(localizeCaughtError(err, t, 'organization.members.limit.error.update'));
    }
  }

  async function handleSave(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (!rateReady) {
      setError(
        displayRateQ.isLoading ? t('keys.limit_loading') : t('keys.limit_error.rate_unavailable'),
      );
      return;
    }
    const parsed = parseSpendLimitInput(value, cnyPerUsd);
    if (!parsed.ok) {
      setError(t(LIMIT_ERROR_KEY[parsed.code]));
      return;
    }
    if (
      parsed.usdCents !== null &&
      (!Number.isSafeInteger(parsed.usdCents) || parsed.usdCents > MEMBER_LIMIT_MAX_USD_CENTS)
    ) {
      setError(t('organization.members.limit.error.invalid'));
      return;
    }
    await submit(parsed.usdCents);
  }

  return (
    <Modal
      isOpen
      onClose={close}
      titleId={titleId}
      descriptionId={descriptionId}
      testId="organization-member-limit-modal"
    >
      <form
        className="flex flex-col gap-4 p-5"
        onSubmit={(event) => void handleSave(event)}
        noValidate
      >
        <header className="flex flex-col gap-1">
          <h3 id={titleId} className="m-0 text-heading text-ink">
            {t('organization.members.limit.title')}
          </h3>
          <p id={descriptionId} className="m-0 text-caption text-ink-low">
            {t('organization.members.limit.description', { member: memberName })}
          </p>
        </header>
        <label className="flex flex-col gap-2">
          <span className="font-mono text-mono-label uppercase text-ink-low">
            {t('organization.members.limit.label')}
          </span>
          <input
            type="text"
            inputMode="decimal"
            value={value}
            onChange={(event) => {
              setDraft(event.currentTarget.value);
              setError(null);
            }}
            disabled={pending || !rateReady}
            placeholder={t('keys.limit.monthly.placeholder')}
            data-testid="organization-member-limit-input"
            aria-invalid={error !== null || undefined}
            className={INPUT_CLASS}
          />
          {rateReady ? null : (
            <span
              className="text-mono-label leading-4 text-warn"
              role="status"
              data-testid="organization-member-limit-rate"
            >
              {displayRateQ.isLoading
                ? t('keys.limit_loading')
                : t('keys.limit_error.rate_unavailable')}
            </span>
          )}
        </label>
        {error !== null ? (
          <p
            className="m-0 text-caption font-medium text-bad"
            role="alert"
            data-testid="organization-member-limit-error"
          >
            {error}
          </p>
        ) : null}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Button
            type="button"
            variant="ghost"
            disabled={pending}
            onClick={() => void submit(null)}
            data-testid="organization-member-limit-clear"
          >
            {t('organization.members.limit.clear')}
          </Button>
          <div className="flex items-center gap-3">
            <Button
              type="button"
              variant="secondary"
              disabled={pending}
              onClick={close}
              data-testid="organization-member-limit-cancel"
            >
              {t('organization.action.cancel')}
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={pending}
              disabled={!rateReady}
              data-testid="organization-member-limit-save"
            >
              {t('organization.action.save')}
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
}
